import { useEffect } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import InfiniteFeedRow from '@cont/SWRInfiniteFeedRow/InfiniteFeedRow';
import FeedTypeBlock from '@cont/SWRInfiniteFeedRow/DynamicFeedTypeBlock';
import {
  isEducatorEntityType,
  isCourseEntityType
} from '@cont/SWRInfiniteFeedRow/utils/getBlockType';
import useTopology from '@api/hooks/topology/useTopology';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useEntireFeed from './useEntireFeed';

const LoaderWrapper = styled.div`
  grid-column: span 18;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const renderLoader = () => (
  <LoaderWrapper>
    <CircularLoader />
  </LoaderWrapper>
);

const EntireFeed = () => {
  const router = useRouter();
  const { goalUID } = router.query;

  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const { topology: goalTopology } = useTopology(goalUID, {
    revalidateOnMount: false
  });

  const { goalSlug } = goalTopology;

  const getLinkAndHrefProps = (type, max, min) => {
    const viewAllLink =
      max > min ? `/goal/${goalSlug}/${goalUID}/free-platform/${type}` : null;
    const viewAllHref = '/goal/[goalSlug]/[goalUID]/free-platform/[type]';

    return {
      viewAllLink,
      viewAllHref
    };
  };

  const {
    data,
    isValidating,
    mutate: refetchEntireFeed,
    fetchMoreData
  } = useEntireFeed({
    goalUID
  });

  useEffect(() => {
    if (isLoggedIn) {
      refetchEntireFeed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const renderItem = (feed, index) => {
    const feedType = feed.type;
    const feedEntityType = feed.extraBlockinfo.entityType;

    let extraProps = {
      goalSlug,
      goalUID,
      index,
      goal: goalTopology,
      lastPrimarySource: 'Old Courses Home',
      type: 'freeCourse',
      dataAnalytics: 'freePlatform'
    };

    if (isCourseEntityType(feedEntityType)) {
      const feedCount = feed.extraBlockinfo?.count || 0;

      extraProps = {
        ...extraProps,
        ...getLinkAndHrefProps(feedType, feedCount, 3)
      };
    }

    if (isEducatorEntityType(feedEntityType)) {
      const feedCount = feed.extraBlockinfo?.count || 0;

      extraProps = {
        ...extraProps,
        ...getLinkAndHrefProps(feedType, feedCount, 0),
        useLeaderboardMetaInfo: true
      };
    }

    return (
      <FeedTypeBlock
        key={feedType}
        feedBlock={feed}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...extraProps}
      />
    );
  };

  return (
    <InfiniteFeedRow
      data={data.results}
      isLoading={isValidating}
      renderItem={renderItem}
      renderLoader={renderLoader}
      onLoadMore={fetchMoreData}
    />
  );
};

export default EntireFeed;
